import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import { Grid2, Paper, TextField, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CampaignSelector, SectionSelector } from "@/shared/selectors";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { useCampaigns } from "@/pages/mill/schedule/CampaignPage";
import { SpareCarrier } from "@/pages/rollshop/models/SpareCarrier";
import { LinearProgressWithLabel } from "@/shared/components/LinearProgressWithLabel";
import { useTableDataLeft, useTableDataRight } from "../hooks/useTableData";
import { useReadyCarriers } from "../SparePage";

const formatSecondsToTime = (
	totalSeconds: number | null | undefined,
): string => {
	if (!totalSeconds) {
		return "00:00:00 hs";
	}

	const isNegative = totalSeconds < 0;
	const absSeconds = Math.abs(totalSeconds);

	const hours = Math.floor(absSeconds / 3600);
	const minutes = Math.floor((absSeconds % 3600) / 60);
	const seconds = absSeconds % 60;

	const pad = (value: number) => String(value).padStart(2, "0");

	const formattedTime = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

	return `${isNegative ? "- " : ""}${formattedTime} hs`;
};

export const TableSpare = () => {
	const [currentCampaignId, setCurrentCampaignId] = useState<number>(1);
	const [error, setError] = useState("");
	const [message, setNotificationMessage] = useState("");

	const { defaultColDef } = useGridDefinitions({});

	const { columnDefsLeft } = useTableDataLeft({ setNotificationMessage });
	const { columnDefsRight } = useTableDataRight({ setNotificationMessage });

	const { data: campaigns } = useCampaigns({
		campaignId: currentCampaignId,
		onError: (error: Error) => setError(error.message),
	});
	const currentCampaign = campaigns ? campaigns[0] : null;

	const { data: currentCarriers, isLoading: currentLoading } = useReadyCarriers(
		{
			campaignId: currentCampaignId,
			onError: (error: Error) => setError(error.message),
		},
	);
	const { data: nextCarriers, isLoading: nextLoading } = useReadyCarriers({
		campaignId: currentCampaignId,
		isNextCampaign: true,
		onError: (error: Error) => setError(error.message),
	});

	const { data: campaignss } = useCampaigns({
		campaignId: nextCarriers ? nextCarriers[0].CampaignId : currentCampaignId,
		onError: (error: Error) => setError(error.message),
	});
	const nextCampaign = campaignss ? campaignss[0] : null;

	const remainingTimeToStart = Math.floor(
		(new Date(nextCampaign?.Start!).getTime() - Date.now()) / 1000,
	);

	return (
		<>
			<Grid2 container justifyContent="center">
				<Grid2 container>
					<Grid2 size={{ md: 12, xs: 12 }} style={{ padding: "0 15px" }}>
						<Typography
							variant="h5"
							style={{ margin: "15px 0 15px", fontWeight: 600 }}
						>
							Spares
						</Typography>
					</Grid2>
					<Grid2 container size={{ md: 6 }} direction="row">
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<CampaignSelector
								label="Current Scheduled"
								value={currentCampaignId}
								onChange={(val: number) => setCurrentCampaignId(val)}
							/>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<SectionSelector
								label="Section"
								value={currentCampaign?.SectionId!}
								disabled
							/>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<TextField
								label="Start"
								variant="outlined"
								value={
									dxtToLocalServerTime(
										currentCampaign?.Start,
										"MM/dd/yy HH:mm",
									) || ""
								}
								disabled
								size="small"
								fullWidth
							/>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<TextField
								label="End"
								variant="outlined"
								value={
									dxtToLocalServerTime(
										currentCampaign?.End,
										"MM/dd/yy HH:mm",
									) || ""
								}
								disabled
								size="small"
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Scheduled Tons"
								//value={currentCampaign?.ScheduledWeight ?? ""}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Actual Tons"
								//value={currentCampaign?.ActualWeight ?? ""}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Scheduled Billets"
								//value={currentCampaign?.ScheduledBillets ?? ""}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Actual Billets"
								//value={currentCampaign?.ActualBillets ?? ""}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
						<Grid2 container alignItems="center" style={{ marginTop: 10 }}>
							<Grid2 size={{ xs: 12, md: 9 }} style={{ padding: "0 15px" }}>
								{/* <LinearProgressWithLabel
									value={
										((currentCampaign?.ActualWeight ?? 0) /
											(currentCampaign?.ScheduledWeight ?? 1)) *
										100
									}
									withLabel
								/> */}
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
								<TextField
									label="Remaining Time"
									value={formatSecondsToTime(
										Math.max(
											Math.floor(
												(new Date(nextCampaign?.End!).getTime() - Date.now()) /
													1000,
											),
											0,
										),
									)}
									variant="outlined"
									size="small"
									disabled
									fullWidth
								/>
							</Grid2>
						</Grid2>
					</Grid2>
					<Grid2 container size={{ md: 6 }} direction="row">
						<Grid2
							size={{ xs: 12, md: 8 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<CampaignSelector
								label="Next Scheduled"
								value={nextCampaign?.CampaignId!}
								onChange={(val: number) => setCurrentCampaignId(val)}
								disabled
							/>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 4 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<SectionSelector
								label="Section"
								value={nextCampaign?.SectionId!}
								disabled
							/>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<TextField
								label="Scheduled Start"
								variant="outlined"
								value={
									dxtToLocalServerTime(nextCampaign?.Start, "MM/dd/yy HH:mm") ||
									""
								}
								disabled
								size="small"
								fullWidth
							/>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ padding: "0 15px", marginBottom: 10 }}
						>
							<TextField
								label="Scheduled End"
								variant="outlined"
								value={
									dxtToLocalServerTime(nextCampaign?.End, "MM/dd/yy HH:mm") ||
									""
								}
								disabled
								size="small"
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Scheduled Tons"
								//value={nextCampaign?.ScheduledWeight ?? ""}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Scheduled Billets"
								//value={nextCampaign?.ScheduledBillets ?? ""}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }} style={{ padding: "0 15px" }}>
							<TextField
								label="Remaining Time to Start"
								value={formatSecondsToTime(remainingTimeToStart)}
								InputProps={{
									style: {
										color:
											remainingTimeToStart < 0
												? "red"
												: remainingTimeToStart < 3600
													? "orange"
													: "inherit",
									},
								}}
								variant="outlined"
								size="small"
								disabled
								fullWidth
							/>
						</Grid2>
					</Grid2>
				</Grid2>
				<Grid2 container style={{ width: "100%", padding: "10px" }} spacing={2}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Paper style={{ height: "70vh", width: "100%" }}>
							<AgGridReact<SpareCarrier>
								loading={currentLoading}
								rowData={currentCarriers || []}
								columnDefs={columnDefsLeft}
								defaultColDef={defaultColDef}
								rowHeight={40}
								headerHeight={34}
								animateRows
								getRowId={(params) =>
									`${params.data.CampaignId}-${params.data.StandId}`
								}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: false,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<div style={{ height: "70vh", width: "100%" }}>
							<AgGridReact<SpareCarrier>
								loading={nextLoading}
								rowData={nextCarriers || []}
								columnDefs={columnDefsRight}
								defaultColDef={defaultColDef}
								rowHeight={40}
								headerHeight={34}
								animateRows
								getRowId={(params) =>
									`${params.data.CampaignId}-${params.data.StandId}`
								}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: false,
									},
								}}
							/>
						</div>
					</Grid2>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
